import { CircleAlert } from 'lucide-react'

import {
  BIAS_COPY,
  dedupeFindings,
  feedbackSummary,
  type BiasSource,
  type CoachingFinding,
} from '../../lib/bias-guardrail'

type BiasCoachBannerProps = {
  findings: CoachingFinding[]
  source?: BiasSource
}

/** Inline coaching — never a silent rewrite. Same title and lead on every surface. */
export function BiasCoachBanner({ findings, source }: BiasCoachBannerProps) {
  const notes = dedupeFindings(findings)
  if (!notes.length) return null
  return (
    <aside
      className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2.5"
      data-bias-guardrail="coach"
      data-source={source ?? 'generate'}
      role="status"
    >
      <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 space-y-1.5 text-sm leading-snug text-muted-foreground">
        <p>
          <span className="font-medium text-foreground">{BIAS_COPY.bannerTitle}</span>
          {' — '}
          {BIAS_COPY.bannerLead}
        </p>
        <p className="text-xs">{BIAS_COPY.recheckLabel}</p>
        <ul className="list-disc space-y-1 pl-4 text-foreground">
          {notes.map((finding) => (
            <li key={finding.id} data-pattern={finding.pattern_id}>
              <strong>{finding.category_label}</strong>
              {': “'}
              {finding.phrase}
              {'” — '}
              {finding.why} {finding.coach}
              {finding.alternative ? ` Try: ${finding.alternative}.` : ''}
            </li>
          ))}
        </ul>
      </div>
    </aside>
  )
}

type FeedbackBiasSectionProps = {
  open: CoachingFinding[]
  incidentCount?: number
}

/** Feedback-report block. Same copy as every other touchpoint. */
export function FeedbackBiasSection({ open, incidentCount = 0 }: FeedbackBiasSectionProps) {
  const notes = dedupeFindings(open)
  return (
    <section
      className="space-y-2 rounded-md border border-border/60 px-3 py-3"
      data-bias-guardrail="feedback-report"
    >
      <h2 className="text-sm font-medium text-foreground">{BIAS_COPY.feedbackTitle}</h2>
      <p className="text-sm text-muted-foreground">{feedbackSummary(notes.length)}</p>
      <p className="text-xs text-muted-foreground">{BIAS_COPY.recheckLabel}</p>
      {notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">{BIAS_COPY.feedbackEmpty}</p>
      ) : (
        <ul className="list-disc space-y-1 pl-4 text-sm">
          {notes.map((finding) => (
            <li key={finding.id}>
              <strong>{finding.category_label}</strong>
              {': “'}
              {finding.phrase}
              {'” — '}
              {finding.why}
            </li>
          ))}
        </ul>
      )}
      {incidentCount > 0 ? (
        <p className="text-xs text-muted-foreground" data-incident-count={incidentCount}>
          {incidentCount} logged check{incidentCount === 1 ? '' : 's'} on this artefact.
        </p>
      ) : null}
    </section>
  )
}
